import { SPRITE_REGISTRY } from "../content/appearance"
import { behaviorRaceFor } from "../character/sprite_definition"
import { HumanoidRaceTypes, Race } from "../character/character"
import { getCurrentTime } from "../time_util"
import {
    AggroResponse,
    AttackPattern,
    Bounds,
    Dimensions,
    Entity,
    EntityDimensions,
    EntityId,
    NpcType,
} from "../models"
import { ItemMechanics } from "./item_mechanics"
import { Mechanics } from "./mechanics"

class Speed {
    static minimum: number = 100
    static maximum: number = 200
    static pctIncrease: number = 0.045
    static maximumSpeedEncumberancePct = 0.2

    /**
     * How long a player must ACTUALLY RUN before the game treats the run as learned.
     *
     * Running means both things at once: the run key held AND the body moving. Pressing the key is
     * not enough and never was a good proxy -- Shift also opens the building shape palette, so a
     * player laying a wall taps it without ever having run a step. That proxy decided two separate
     * things, and got both wrong: whether to RAISE the camp's run hint (server
     * campaigns/ember/run_hint.ts, via `Entity.hasBoosted`) and whether to DISMISS it (client
     * player.ts). A hint that vanishes before it has been read teaches nobody.
     *
     * A second is long enough to be a deliberate act rather than a stray keypress, and short enough
     * that nobody who meant to run has to hold it twice.
     *
     * Lives in common because both ends measure it and they must not drift: the client dismisses the
     * clue locally (no round trip, so the hint goes the moment the player has earned it) while the
     * server independently decides whether to raise it at all.
     */
    static runLearnedMs = 1000

    static perPointIncrease = () => {
        const range = this.range()
        const upgradeAmount = Math.round(range * this.pctIncrease)
        return upgradeAmount
    }

    static maximumNumberOfUpgrades = () => {
        return Math.floor(this.maximum / this.perPointIncrease())
    }

    static increaseCostFrom = (speed: number) => {
        const diffFromMinimum = speed - this.minimum
        const numberOfInceases = Math.floor(diffFromMinimum / this.perPointIncrease())
        return Math.max(1, Math.floor(Math.log2(numberOfInceases)))
    }

    static maxNormalizedSpeed = () => Math.floor((this.maximum - this.minimum) / this.perPointIncrease())

    static toNormalizedSpeed = (rawSpeed: number) => Math.floor((rawSpeed - this.minimum) / this.perPointIncrease())

    private static range = () => this.maximum - this.minimum

    static toNormalizedSpeedPctOfMax = (rawSpeed: number) => (rawSpeed - this.minimum) / this.range()

    static toNormalizedSpeedPctOfMin = (rawSpeed: number) => rawSpeed / this.minimum

    static fromPctOfMax = (pct: number) => this.minimum + this.range() * pct

    static upgrade = (fromSpeed: number) => {
        if (fromSpeed >= this.maximum) {
            return
        }
        const upgradeAmount = this.perPointIncrease()
        let updatedMax = fromSpeed + upgradeAmount
        const diff = this.maximum - updatedMax
        if (diff < upgradeAmount) {
            updatedMax = this.maximum
        }

        if (updatedMax > this.maximum) {
            return
        }

        return updatedMax
    }

    static needsSpeedRecomputation = (entity: Entity, lastComputationTs: number) => {
        const toCmpare = Math.max(entity.inventory.lastUpdateTs || 0, entity.lastStatsUpdateTs || 0)
        return toCmpare > lastComputationTs
    }

    static effectiveSpeed = (entity: Entity) => {
        if (entity.isPlayer()) {
            const baselineSpeed = entity.boosted
                ? entity.maxSpeed + Math.floor(entity.maxSpeed * Math.min(1.0, 0.6 + entity.boostPct))
                : entity.maxSpeed

            const weightSpeedReductionBudget = this.maximumSpeedEncumberancePct * this.maximum
            const weightPctOfMax = ItemMechanics.weight.computeWeightPctOfMax(entity.inventory)
            const speedReduction = Math.min(weightSpeedReductionBudget * weightPctOfMax, baselineSpeed * 0.65)

            const weightBased = baselineSpeed - speedReduction
            const speed = weightBased * (entity.movingBackwards ? 0.7 : 1.0) * (entity.weaponCharge?.movementMultiplier ?? 1)

            const isSlowed = Mechanics.effect.hasEntityEffect(entity, "webslow")
            const isPushSlowed = Mechanics.effect.hasEntityEffect(entity, "pushslow")

            if (isSlowed) {
                return speed * 0.2
            } else if (isPushSlowed) {
                const pushSlowEffect = Mechanics.effect.getEntityEffect(entity, "pushslow")
                const speedMultiplier = pushSlowEffect?.speedMultiplier || 0.7
                return speed * speedMultiplier
            }

            return speed
        } else {
            // for now ... entities aren't affected by boosts or weight
            const speed = entity.speed * (entity.movingBackwards ? 0.7 : 1.0) * (entity.weaponCharge?.movementMultiplier ?? 1)
            const isSlowed = Mechanics.effect.hasEntityEffect(entity, "webslow")
            return isSlowed ? speed * 0.2 : speed
        }
    }
}

class HitPoints {
    static minimum: number = 3
    static maximum: number = 50

    private static range = () => this.maximum - this.minimum

    static perPointIncrease = () => 1

    static increaseCostFrom = (hp: number) => {
        const diffFromMinimum = hp - this.minimum
        const numberOfInceases = Math.floor(diffFromMinimum / this.perPointIncrease())
        return Math.max(1, Math.floor(Math.log2(numberOfInceases)))
    }

    static upgrade = (fromHp: number) => {
        if (fromHp >= this.maximum) {
            return
        }
        const upgradeAmount = this.perPointIncrease()
        let updatedMax = fromHp + upgradeAmount
        const diff = this.maximum - updatedMax
        if (diff < upgradeAmount) {
            updatedMax = this.maximum
        }

        if (updatedMax > this.maximum) {
            return
        }

        return updatedMax
    }

    static fromPctOfMax = (pct: number) => this.minimum + this.range() * pct
}

class Levels {
    static maximum = 600
    private static upgradePointsForLevel = (level: number): number => {
        return Math.max(1, Math.floor(Math.log1p(level)))
    }

    static totalUpgradePointsPerLevel: Record<number, number> = {}
    static ugradePointsAtLevel: Record<number, number> = {}
    static {
        let totalUpgradePoints = 0
        for (let i = 1; i <= this.maximum; i++) {
            const upgradePoints = this.upgradePointsForLevel(i)
            this.ugradePointsAtLevel[i] = upgradePoints
            totalUpgradePoints += upgradePoints
            this.totalUpgradePointsPerLevel[i] = totalUpgradePoints
        }
    }
}

class MinMax {
    maximum = 100
    minimum = 0

    constructor(min: number, max: number) {
        this.minimum = min
        this.maximum = max
    }

    range = () => this.maximum - this.minimum
    fromPctOfMax = (pct: number) => this.minimum + this.range() * pct
    toPctOfMax = (aggression: number) => aggression / this.maximum
}

class Inventory {
    private static inventoryBearingNpcTypes: Set<NpcType> = new Set()
    static {
        this.inventoryBearingNpcTypes.add("zombie")
        this.inventoryBearingNpcTypes.add("outpost")
    }

    static canEquipWeapon = (entity: Entity) => {
        if (["zombie"].includes(entity.npcType)) {
            return true
        }

        return false
    }

    static hasEquipment = (entity: Entity) => {
        if (entity.npcType) {
            if (entity.flags.forcePersist) {
                return true
            }
            if (!this.inventoryBearingNpcTypes.has(entity.npcType)) {
                return false
            }
            if (entity.npcType === "outpost") {
                return true
            }
        }

        // for now ... only humanoids can have equipment
        // if (!Body.isHumanoid(entity?.appearance?.race)) {
        //     return false
        // }

        return true
    }
}

class Body {
    static isStationary = (entity: Entity) => {
        if (["spikes1"].includes(entity?.appearance?.race)) {
            return true
        }
        return false
    }

    static isHumanoid = (race: Race) => {
        return (HumanoidRaceTypes as unknown as string[]).includes(race)
    }

    static isShort = (entity: Entity) => {
        return !this.isHumanoid(entity?.appearance?.race)
    }

    static isFlat = (entity: Entity) => {
        const race = behaviorRaceFor(entity?.appearance?.race)
        if (!race) {
            return false
        }
        return ["spikes1"].includes(race)
    }

    static isSmall = (entity: Entity) => {
        const race = behaviorRaceFor(entity?.appearance?.race)
        if (!race) {
            return false
        }

        if (this.isHumanoid(race as Race)) {
            return false
        }

        return ["slime", "rabbit", "chicken", "snake", "wasp"].includes(race)
    }

    static hasAppearance = (npcType: NpcType) => {
        return !["outpost", "collectable", "marker"].includes(npcType)
    }

    static dimensions = (entity: Entity): Dimensions => {
        if (this.isHumanoid(entity?.appearance?.race)) {
            return {
                w: 25,
                h: 50,
            }
        }

        return EntityDimensions[entity.entityType]
    }

    static calculateCollisionBounds = (entity: Entity, bounds: Bounds): boolean => {
        const isHorselike = ["deer", "horse"].includes(behaviorRaceFor(entity.appearance?.race) || "")
        const x = entity.location.x
        const y = entity.location.y

        if (isHorselike) {
            const direction = entity.getGrossOrientation()
            if (direction === "LeftRight") {
                bounds.x1 = x - entity.dimensions.w
                bounds.y1 = y - entity.dimensions.h * 1.5
                bounds.x2 = x + entity.dimensions.w
                bounds.y2 = y + entity.dimensions.h * 0.5
            } else {
                bounds.x1 = x - entity.dimensions.w / 2
                bounds.y1 = y - entity.dimensions.h * 1.5
                bounds.x2 = x + entity.dimensions.w / 2
                bounds.y2 = y + entity.dimensions.h / 2
            }

            return true
        }

        return false
    }

    static calculateZIndexBoost = (entity: Entity): number => {
        if (entity.npcType === "collectable") {
            return 0
        } else {
            // Standing bodies share the wall sprites' ground-depth origin. A size/race
            // penalty puts small creatures behind walls they have already walked in front of.
            return 100 + entity.location.y + (SPRITE_REGISTRY.body?.[entity.appearance?.race]?.depthBias ?? 0)
        }
    }
}

class Psych {
    private static hostilityMinMax = new MinMax(0, 100)

    static hostility = () => this.hostilityMinMax

    static attackPattern = (race: Race): AttackPattern => Mechanics.population.psych(race)?.attackPattern || "default"

    static aggroResponse = (race: Race): AggroResponse => Mechanics.population.psych(race)?.aggroResponse || "default"

    static closeOnStun = (race: Race): boolean => Mechanics.population.psych(race)?.closeOnStun
}

// The set of interaction verbs an entity can offer. Historically this order was also the on-screen
// stacking order of the hover icons; the icons are gone (a tap on the entity activates its primary
// interaction, two or more open the interaction wheel), so the order is now only a stable fallback
// for ties in InteractionMetas priority. Append new types rather than inserting.
// "food" is the ember fire-recruit's "give me a meal" ask. "descend" is a way into another room --
// an ember camp's mine entrance.
export const InteractionTypes = ["burn", "take", "chat", "inventory", "crown", "button", "command", "food", "help", "descend"] as const

export type InteractionType = (typeof InteractionTypes)[number]

// One offered interaction, as the server reports it to a viewer. `type` is what
// ACTIVATE_ENTITY_INTERACTION carries and what scripts/campaigns match on; everything else exists
// so the client can decide what a bare tap does and what to print on a wheel slice.
//
// The same type means different things on different entities -- "take" opens a chest but PICKS UP
// (and extinguishes) a campfire -- so label/priority/neverPrimary are per-entity overrides of the
// InteractionMetas defaults, supplied at the point the interaction is added.
export interface InteractionDescriptor {
    type: InteractionType
    // Wheel slice text. Defaults to InteractionMetas[type].label.
    label?: string
    // Higher sorts closer to "primary". Defaults to InteractionMetas[type].priority.
    priority?: number
    // Costly or destructive: never fired by a bare tap, only by picking it out of the wheel.
    // Defaults to InteractionMetas[type].neverPrimary.
    neverPrimary?: boolean
    // A held verb begins on pointer-down, refreshes while held and cancels on release.
    holdDurationMs?: number
}

// The presentation half of a descriptor: what a caller may say about a verb when it adds it.
export type InteractionPresentation = Omit<InteractionDescriptor, "type">

// Defaults per interaction type. `chat` sits at the bottom because the DENIZEN campaign adds it to
// every unmuted denizen, so it is the one verb an entity almost never wants as its primary when it
// has a script verb of its own. `food` hands over a real inventory item, so it is never primary.
export const InteractionMetas: Record<InteractionType, Required<Omit<InteractionDescriptor, "type" | "holdDurationMs">>> = {
    take: { label: "Take", priority: 70, neverPrimary: false },
    inventory: { label: "Items", priority: 65, neverPrimary: false },
    burn: { label: "Light", priority: 60, neverPrimary: false },
    button: { label: "Use", priority: 55, neverPrimary: false },
    crown: { label: "Crown", priority: 50, neverPrimary: false },
    command: { label: "Orders", priority: 45, neverPrimary: false },
    food: { label: "Give Food", priority: 40, neverPrimary: true },
    chat: { label: "Talk", priority: 10, neverPrimary: false },
    help: { label: "Hold to help up", priority: 80, neverPrimary: false },
    // High, and a bare tap takes it: standing at a hole in the ground, going in is the only thing
    // you came for. The label is overridden per entity ("Enter the mine").
    descend: { label: "Go down", priority: 75, neverPrimary: false },
}

// What a tap on the entity should do. "menu" carries the full ranked list (including neverPrimary
// entries, which are selectable from the wheel even though they can't be a bare-tap primary).
export type InteractionResolution =
    | { kind: "none" }
    | { kind: "single"; interaction: InteractionDescriptor }
    | { kind: "menu"; interactions: InteractionDescriptor[] }

export interface EntityInteractions {
    specific: Map<EntityId, InteractionDescriptor[]>
    interactions: InteractionDescriptor[]
}

export const interactionLabel = (descriptor: InteractionDescriptor): string =>
    descriptor.label || InteractionMetas[descriptor.type]?.label || descriptor.type

export const interactionPriority = (descriptor: InteractionDescriptor): number =>
    descriptor.priority ?? InteractionMetas[descriptor.type]?.priority ?? 0

export const interactionNeverPrimary = (descriptor: InteractionDescriptor): boolean =>
    descriptor.neverPrimary ?? InteractionMetas[descriptor.type]?.neverPrimary ?? false

// ===== held verbs =====
//
// A HELD verb is performed by keeping the button down on the entity: it begins on pointer-down,
// refreshes while held and cancels on release (see HeldInteraction on the server). That gesture is
// the whole interaction, which has two consequences.
//
// IT CANNOT BE A WHEEL SLICE. Picking it out of a menu sends one activation with no hold phase at
// all, and every held handler ignores that -- so the slice is a dead button that teaches the player
// the interaction is broken. And an entity whose verbs are ALL held therefore has no menu to open:
// the injured ember survivor offers "hold to help up" and nothing else, so her wheel was one dead
// slice over a body the player was meant to be holding down on instead.
//
// A held verb is also never the thing a bare tap fires: PlayerController arbitrates the press
// before any of this (a press on a held target starts the hold), which is why `resolveInteractions`
// below is free to ignore the distinction.
export const interactionIsHeld = (descriptor: InteractionDescriptor): boolean => descriptor.holdDurationMs > 0

export const wheelInteractions = (interactions: InteractionDescriptor[]): InteractionDescriptor[] =>
    interactions.filter(descriptor => !interactionIsHeld(descriptor))

// ===== reach =====
//
// How close a player has to be to the entity for a verb that moves a real object between them.
//
// It exists because an interaction can now be reached from a HUD CARD as well as from the entity
// itself (the ember roster's visitor card), and a card has no distance at all -- without this, a
// stranger at the fire could be fed, hired and put to work from the far side of the forest, which
// is most of the camp's loop answered by two taps on a corner of the screen.
//
// Twelve tiles rather than arm's length on purpose: the rule being enforced is the one a world tap
// ALREADY enforced by accident -- you have to be able to see them. Anything on screen is inside
// this, so tapping an entity in the world behaves exactly as it always has, and only the card (and
// only for a verb that hands something over) is newly bounded.
//
// In TILES, and multiplied by TileSize at the call site, deliberately: this module and ../models
// import each other, so a `12 * TileSize` evaluated here at module scope reads TileSize before
// models has finished initialising and throws on load ("Cannot access 'TileSize' before
// initialization") -- in the bundled client only, where the cycle resolves in the other order.
export const INTERACTION_REACH_TILES = 12

// Which verbs are bounded by it. Only the ones that move a real item out of the player's own pack:
// talking, ordering and opening a thing you are looking at are all fine at a distance, and two of
// them are reachable from the roster precisely so they work when you cannot get there.
export const interactionNeedsReach = (type: InteractionType): boolean => type === "food"

// Accepts either the descriptor list the server sends today or a bare InteractionType[] (what the
// wire carried before descriptors, and what a test or a hand-written call site may still pass).
// Unknown types are dropped rather than trusted, since they'd have no label and no handler.
export const normalizeInteractions = (
    interactions: (InteractionDescriptor | InteractionType)[] | undefined | null,
): InteractionDescriptor[] => {
    if (!interactions) {
        return []
    }
    const normalized: InteractionDescriptor[] = []
    interactions.forEach(next => {
        const descriptor: InteractionDescriptor =
            typeof next === "string" ? { type: next } : next && typeof next === "object" ? next : undefined
        if (!descriptor?.type || !InteractionTypes.includes(descriptor.type)) {
            return
        }
        normalized.push(descriptor)
    })
    return normalized
}

// Most-primary first. Ties fall back to InteractionTypes order so the ranking is stable frame to
// frame (an unstable order would make the wheel's slices jump between opens).
export const rankInteractions = (interactions: InteractionDescriptor[]): InteractionDescriptor[] =>
    [...interactions].sort((a, b) => {
        const byPriority = interactionPriority(b) - interactionPriority(a)
        if (byPriority !== 0) {
            return byPriority
        }
        return InteractionTypes.indexOf(a.type) - InteractionTypes.indexOf(b.type)
    })

// The tap contract, in one place so the client and its tests agree:
//   - no interactions          -> "none": the tap falls through to the world action (attack/build)
//   - exactly one, tap-safe    -> "single": fire it
//   - anything else            -> "menu": open the interaction wheel over the entity
//
// Two or more verbs always go to the wheel, which is also what keeps every verb reachable: were a
// lone tap-eligible verb to win outright, an entity offering "Talk" plus the wheel-only "Give Food"
// would leave the food unreachable. A single neverPrimary verb still opens a one-slice wheel, so a
// costly action (hand over a drumstick, pocket a lit campfire) always takes a deliberate pick.
export const resolveInteractions = (
    interactions: (InteractionDescriptor | InteractionType)[] | undefined | null,
): InteractionResolution => {
    const ranked = rankInteractions(normalizeInteractions(interactions))
    if (ranked.length === 0) {
        return { kind: "none" }
    }
    if (ranked.length === 1 && !interactionNeverPrimary(ranked[0])) {
        return { kind: "single", interaction: ranked[0] }
    }
    return { kind: "menu", interactions: ranked }
}

class Flags {
    static updateFlag = (entity: Entity, flags: any) => {
        const updateFlags = (entity: Entity, flag: string) => {
            const value = flags[flag]
            if (value === null) {
                // delete flag
                delete entity.flags[flag]
            } else if (typeof value !== "string") {
                entity.flags[flag] = value
            } else {
                const result = value.matchAll(/\{(.*?)\}/g).next().value
                if (result) {
                    const matched = result[1]
                    const [operand, impact] = matched.split(":")
                    if (operand === "increment") {
                        const updatedValue = (entity.flags[flag] || 0) + parseInt(impact)
                        entity.flags[flag] = updatedValue
                    }
                    if (operand === "decrement") {
                        const updatedValue = Math.max(0, (entity.flags[flag] || 0) - parseInt(impact))
                        entity.flags[flag] = updatedValue
                    }
                    if (operand === "timestamp") {
                        entity.flags[flag] = getCurrentTime() + parseInt(impact)
                    }
                } else {
                    entity.flags[flag] = value
                }
            }
        }

        if (entity) {
            Object.keys(flags).forEach(flag => {
                updateFlags(entity, flag)
            })
        }

        return entity
    }
}

class Interactions {}

export class EntityMechanics {
    static inventory = Inventory
    static body = Body
    static speed = Speed
    static hitPoints = HitPoints
    static levels = Levels
    static psych = Psych
    static interactions = Interactions
    static flags = Flags
}
